import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Box, Typography, Dialog, DialogTitle, IconButton } from "@mui/material";
import AlertNotification from "./AlertNotification";
import LinkIcon from "@mui/icons-material/Link";
import EmailIcon from "@mui/icons-material/Email";
import CloseIcon from "@mui/icons-material/Close";
import AttachmentIcon from "@mui/icons-material/Attachment";
import DownloadIcon from "@mui/icons-material/Download";

// Re-encode an image blob to PNG via canvas. Clipboard image writes are only
// reliably supported for PNG, and tapestries are now served as JPG (TT-935),
// so convert before writing to the clipboard. A PNG source passes through fine.
const blobToPng = (blob) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob((out) => (out ? resolve(out) : reject(new Error("toBlob failed"))), "image/png");
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

const styles = {
  shareOption: {
    display: "flex",
    alignItems: "center",
    marginBottom: "12px",
    cursor: "pointer",
    color: "var(--color-text-primary)",
    "&:hover p": {
      color: "var(--color-accent-primary)",
    },
  },
  iconStyle: {
    color: "var(--color-accent-primary)",
    marginRight: "8px",
  },
  dialogBox: {
    padding: "20px",
    paddingTop: "10px",
  },
  closeIcon: {
    position: "absolute",
    right: 8,
    top: 12,
    color: (theme) => theme.palette.grey[500],
  },
};

/**
 * Pop-up modal to containing options for sharing a tapestry.
 *
 * Leverages Material UI Component Modal.
 *
 * @constructor
 * @param {boolean} open -  Boolean to control whether the modal is open or closed
 * @param {function} handleClose - Function to close the modal
 * @param {string} tapShareUrl - String containing the tapestry URL
 * @param {string} title - String containing the document or meeting title
 */

const ShareModal = ({ open, handleClose, tapShareUrl, title }) => {
  const [alertOpen, setAlertOpen] = useState(false);
  const [imageBlob, setImageBlob] = useState(null);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(tapShareUrl);
    setAlertOpen(true);
    handleClose();
  }, [handleClose, tapShareUrl]);

  const emailLink = useMemo(() => `mailto:?subject=Tapestry - ${title}&body=${tapShareUrl}`, [title, tapShareUrl]);

  const downloadImage = useCallback(() => {
    const imageURL = URL.createObjectURL(imageBlob);
    const ext = imageBlob?.type === "image/png" ? "png" : "jpg";
    const link = document.createElement("a");
    link.href = imageURL;
    link.download = `${title}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [title, imageBlob]);

  const copyImageToClipboard = useCallback(async () => {
    try {
      const pngBlob = imageBlob?.type === "image/png" ? imageBlob : await blobToPng(imageBlob);
      await navigator.clipboard.write([
        new ClipboardItem({
          "image/png": pngBlob,
        }),
      ]);
      setAlertOpen(true);
      handleClose();
    } catch (error) {
      console.error(error);
    }
  }, [handleClose, imageBlob]);

  useEffect(() => {
    async function downloadImage() {
      const cacheBuster = `${tapShareUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;
      const image = await fetch(tapShareUrl + cacheBuster);
      const blob = await image.blob();
      setImageBlob(blob);
    }
    if (open && tapShareUrl) {
      downloadImage();
    }
  }, [tapShareUrl, open]);

  const res = useMemo(
    () => (
      <div>
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>
            Share
            <IconButton aria-label="close" onClick={handleClose} sx={styles.closeIcon}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={styles.dialogBox}>
            <Box sx={styles.shareOption} onClick={copyToClipboard}>
              <LinkIcon sx={styles.iconStyle} />
              <Typography>Copy link to clipboard</Typography>
            </Box>
            <a href={emailLink} style={{ textDecoration: "none" }} target="_blank" rel="noreferrer">
              <Box sx={styles.shareOption} onClick={handleClose}>
                <EmailIcon sx={styles.iconStyle} />
                <Typography>Share via email</Typography>
              </Box>
            </a>
            {imageBlob && (
              <Box sx={styles.shareOption} onClick={downloadImage}>
                <DownloadIcon sx={styles.iconStyle} />
                <Typography>Download image</Typography>
              </Box>
            )}
            {imageBlob && (
              <Box sx={{ ...styles.shareOption, marginBottom: 0 }} onClick={copyImageToClipboard}>
                <AttachmentIcon sx={styles.iconStyle} />
                <Typography>Copy image to clipboard</Typography>
              </Box>
            )}
          </Box>
        </Dialog>
        <AlertNotification message="Copied to clipboard" severity="success" open={alertOpen} setOpen={setAlertOpen} />
      </div>
    ),
    [alertOpen, copyImageToClipboard, copyToClipboard, downloadImage, emailLink, handleClose, imageBlob, open]
  );

  return res;
};

export default ShareModal;
